import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAutoSaveNote } from '../../hooks/useEncryptedNotes';
import { NoteEditor } from '../../components/notes/NoteEditor';
import { Button, Spinner } from '@theraply/ui';
import {
  ArrowLeftIcon,
  TrashIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

export function SessionNotesPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const parsedBookingId = bookingId ? parseInt(bookingId, 10) : 0;

  const {
    content,
    setContent,
    isSaving,
    error,
    lastSaved,
    manualSave,
  } = useAutoSaveNote(parsedBookingId);

  if (!bookingId || isNaN(parsedBookingId)) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-600">Invalid booking ID</p>
      </div>
    );
  }

  const handleDelete = async () => {
    // TODO: Implement delete functionality
    setShowDeleteConfirm(false);
    navigate('/notes');
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Session Notes</h1>
              <p className="text-sm text-gray-600">Booking #{bookingId}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* E2E Encryption Badge */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
              <ShieldCheckIcon className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-green-700">
                End-to-end encrypted
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-600 hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </header>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 px-6 py-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <NoteEditor
          content={content}
          onChange={setContent}
          onSave={manualSave}
          isSaving={isSaving}
          lastSaved={lastSaved}
          placeholder="Write your confidential session notes here. All notes are encrypted end-to-end and only you can read them."
        />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Session Note?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              This will permanently delete this session note. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Note
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
